/**
 * Branch-scoped number formatting foundation.
 * Example: formatBranchNumber('LH1', 1) -> 'LH1-000001'
 *
 * The value itself comes from a row-locked database counter (branch_counters),
 * allocated server-side. Clients never participate in numbering.
 */
export const SEQUENCE_KINDS = ['TXN', 'PUR', 'EXP', 'HOF', 'CRV'] as const;
export type SequenceKind = (typeof SEQUENCE_KINDS)[number];

/** Phase 2F - delivery workflow states (separate from the linear laundry workflow). */
export const DELIVERY_STATUSES = [
  'PENDING',
  'ASSIGNED',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'DELIVERY_DONE',
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Human-readable delivery states used in UIs and audit metadata. */
export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  PENDING: 'Pending assignment',
  ASSIGNED: 'Assigned',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  DELIVERY_DONE: 'Delivery done',
};

/** Delivery lifecycle checkpoint kinds (server-side state machine). */
export const DELIVERY_TRANSITION_KINDS = [
  'STARTED',
  'COMPLETED',
  'CLOSED',
] as const;
export type DeliveryTransitionKind = (typeof DELIVERY_TRANSITION_KINDS)[number];

/**
 * Allowed successors for each delivery state - the server-validated edge
 * matrix for the delivery workflow (mirrors TRANSACTION_STATUS_SUCCESORS for
 * the pickup workflow, but lives here so the two lifecycles stay separate).
 * Linear by design: no skipping, no backwards moves, and DELIVERY_DONE is
 * terminal. The delivery lifecycle never adds values to TRANSACTION_STATUSES.
 */
export const DELIVERY_STATUS_SUCCESORS: Record<DeliveryStatus, DeliveryStatus[]> = {
  PENDING: ['ASSIGNED'],
  ASSIGNED: ['OUT_FOR_DELIVERY'],
  OUT_FOR_DELIVERY: ['DELIVERED'],
  DELIVERED: ['DELIVERY_DONE'],
  DELIVERY_DONE: [],
};

/** True when from -> to is a legal delivery edge (server-side validation). */
export function isValidDeliveryTransition(from: DeliveryStatus, to: DeliveryStatus): boolean {
  return DELIVERY_STATUS_SUCCESORS[from]?.includes(to) ?? false;
}

export const TRANSACTION_NUMBER_WIDTH = 6;

/**
 * Document numbers (Phase 2E): TXN stays bare (`LH1-000001`), while
 * financial documents carry their kind as a prefix:
 *   PUR -> `PUR-LH1-000001`, EXP -> `EXP-LH1-000001`.
 * Allocated server-side from the same row-locked branch counters.
 */
export function formatSequenceNumber(
  kind: SequenceKind,
  branchCode: string,
  value: number,
): string {
  const base = formatBranchNumber(branchCode, value);
  return kind === 'TXN' ? base : `${kind}-${base}`;
}

/** Matches PUR-LH1-000001 / EXP-LH2-000042 / HOF-LH1-000003 / CRV-LH1-000007 style document numbers. */
export const DOCUMENT_NUMBER_REGEX = /^(PUR|EXP|HOF|CRV)-([A-Z0-9]+)-(\d{6,})$/;

export function formatBranchNumber(
  branchCode: string,
  value: number,
  width: number = TRANSACTION_NUMBER_WIDTH,
): string {
  if (!/^[A-Z0-9]+$/.test(branchCode)) {
    throw new Error(`Invalid branch code: ${branchCode}`);
  }
  if (!Number.isInteger(value) || value < 1) {
    throw new Error(`Invalid sequence value: ${value}`);
  }
  return `${branchCode}-${String(value).padStart(width, '0')}`;
}

export const BRANCH_NUMBER_REGEX = /^([A-Z0-9]+)-(\d{6,})$/;

/**
 * Transaction statuses (foundation for future workflow phases).
 * Stored as plain strings + history table so future statuses
 * (e.g. delivery) can be added without destructive migrations.
 */
export const TRANSACTION_STATUSES = [
  'PENDING',
  'WASHING',
  'FOLDING',
  'READY',
  'RELEASED',
  'DONE',
] as const;

export type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

/** Human labels for statuses (UI + reports). */
export const TRANSACTION_STATUS_LABELS: Record<TransactionStatus, string> = {
  PENDING: 'Pending',
  WASHING: 'Washing',
  FOLDING: 'Folding',
  READY: 'Ready',
  RELEASED: 'Released',
  DONE: 'Done',
};

/** Allowed successors for each status (workflow matrix foundation, Phase 2+). */
export const TRANSACTION_STATUS_SUCCESORS: Record<TransactionStatus, TransactionStatus[]> = {
  PENDING: ['WASHING'],
  WASHING: ['FOLDING'],
  FOLDING: ['READY'],
  READY: ['RELEASED'],
  RELEASED: ['DONE'],
  DONE: [],
};
